import cn from 'classnames';
import Image from 'next/image';
import { FormattedMessage, useIntl } from 'react-intl';
import { FC } from 'react';

type IllustrationVariant = 'generic' | 'dog' | 'cat';

interface EmptyCollectionStateProps {
  className?: string;
  variant?: IllustrationVariant;
}

const illustrationMap: Record<IllustrationVariant, string> = {
  generic: '/images/pet-profile/empty-state-white-bg-generic.png',
  dog: '/images/pet-profile/empty-state-dark-bg-dog.png',
  cat: '/images/pet-profile/empty-state-dark-bg-cat.png',
};

/**
 * Displays an empty state message when no products match the selected filters.
 * Shows an illustration and prompts users to reduce their filter selection.
 */
export const EmptyCollectionState: FC<EmptyCollectionStateProps> = ({
  className,
  variant = 'generic',
}) => {
  const illustrationSrc = illustrationMap[variant];
  const { formatMessage } = useIntl();

  return (
    <div
      className={cn(
        'col-span-1 flex flex-col items-center justify-center text-center leading-6 lg:col-span-3',
        className
      )}
      data-test="empty-collection-state"
    >
      <Image
        src={illustrationSrc}
        alt={formatMessage({ id: 'pet-profile:empty-collection:image-alt' })}
        width={200}
        height={200}
        className="mb-4 object-contain"
        data-test="empty-collection-illustration"
      />
      <h3
        className={cn(
          'font-black',
          variant === 'generic' ? 'text-text-light-bg-primary' : 'text-text-dark-bg-primary'
        )}
        data-test="empty-collection-title"
      >
        <FormattedMessage id="pet-profile:empty-collection:title" />
      </h3>
      <p
        className={cn(
          variant === 'generic' ? 'text-text-light-bg-secondary' : 'text-text-dark-bg-secondary'
        )}
        data-test="empty-collection-description"
      >
        <FormattedMessage id="pet-profile:empty-collection:description" />
      </p>
    </div>
  );
};

export default EmptyCollectionState;
